import { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../../../AuthContext';
import { DEFAULT_GATE_SCHEDULE } from '../constants';

const useSettings = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isModified, setIsModified] = useState(false);
    const [serverKioskUI, setServerKioskUI] = useState(null);
    const { globalTenantId, user } = useContext(AuthContext);
    const [value, setValue] = useState(0);
    const [kiosks, setKiosks] = useState([]);
    const [kioskTypes, setKioskTypes] = useState([]);
    const [kioskThemes, setKioskThemes] = useState([]);
    
    // Dialog states
    const [openWarningDialog, setOpenWarningDialog] = useState(false);
    const [openRevertWarningDialog, setOpenRevertWarningDialog] = useState(false);
    const [showThemeDialog, setShowThemeDialog] = useState(false);
    const [showResetBluetoothDialog, setShowResetBluetoothDialog] = useState(false);
    const [showDiffDialog, setShowDiffDialog] = useState(false);
    const [showLockdownAppSettingsDialog, setShowLockdownAppSettingsDialog] = useState(false);
    const [showMovementZonesDialog, setShowMovementZonesDialog] = useState(false);
    const [showANPRMovementZonesDialog, setShowANPRMovementZonesDialog] = useState(false);
    const [showDebugLogDialog, setShowDebugLogDialog] = useState(false);
    
    // Bluetooth states
    const [bluetoothResetOption, setBluetoothResetOption] = useState('basic');
    const [bluetoothCustomPasscode, setBluetoothCustomPasscode] = useState(null);
    
    // Alias states
    const [disableNumberPlateAliases, setDisableNumberPlateAliases] = useState(false);
    const [disablePINAliases, setDisablePINAliases] = useState(false);
    const [disableQrCodeKeysAliases, setDisableQrCodeKeysAliases] = useState(false);
    const [disableFaceProfileAliases, setDisableFaceProfileAliases] = useState(false);
    const [masterPlateAliases, setMasterPlateAliases] = useState(null);
    const [masterPINAliases, setMasterPINAliases] = useState(null);
    const [masterQrCodeKeyAliases, setMasterQrCodeKeyAliases] = useState(null);
    const [masterFaceProfileAliases, setMasterFaceProfileAliases] = useState(null);
    const [masterTenantNameForNumberPlates, setMasterTenantNameForNumberPlates] = useState(null);
    const [masterTenantNameForPINs, setMasterTenantNameForPINs] = useState(null);
    const [masterTenantNameForQrCodeKeys, setMasterTenantNameForQrCodeKeys] = useState(null);
    const [masterTenantNameForFaceProfiles, setMasterTenantNameForFaceProfiles] = useState(null);
    
    // Gallery and accordion states
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [isKioskUIGalleryOpen, setIsKioskUIGalleryOpen] = useState(false);
    const [kioskUITextareaRef, setKioskUITextareaRef] = useState(null);
    const [kioskUICursorPosition, setKioskUICursorPosition] = useState(null);
    const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
    
    // Theme states
    const [customKioskThemeID, setCustomKioskThemeID] = useState(null);
    const [previousCustomKioskThemeID, setPreviousCustomKioskThemeID] = useState(null);
    
    // Gate schedule state
    const [gateSchedule, setGateSchedule] = useState(DEFAULT_GATE_SCHEDULE);
    const [gateTimesDialogOpen, setGateTimesDialogOpen] = useState(false);
    
    // Debug log states
    const [debugLog, setDebugLog] = useState('');
    const [loadingDebugLog, setLoadingDebugLog] = useState(false);

    // Reset isModified when globalTenantId changes
    useEffect(() => {
        setIsModified(false);
    }, [globalTenantId]);

    const setServerKioskUIFromAPI = async (forceKioskTypeId) => {
        let apiUrl = `/api/kioskUI?tenantId=${globalTenantId}`;
        if (forceKioskTypeId) {
            apiUrl += `&forceKioskTypeId=${forceKioskTypeId}`;
        }
        try {
            let result = await axios.get(apiUrl);
            setServerKioskUI(result.data.kioskUI);
            return result.data.kioskUI;
        } catch (error) {
            console.error('Error fetching default Kiosk UI:', error);
        }
    };

    const handleUnload = useCallback((e) => {
        if (isModified) {
            const message = 'You have unsaved changes. Are you sure you want to leave?';
            e.preventDefault();
            e.returnValue = message;
            return message;
        }
    }, [isModified]);

    useEffect(() => {
        if (globalTenantId == null) return;
        window.addEventListener('beforeunload', handleUnload);
        
        // Reset schedule to default when tenant changes
        setGateSchedule(DEFAULT_GATE_SCHEDULE);

        axios.get('/api/tenants', { params: { tenantId: globalTenantId } })
            .then(result => {
                setLoading(false);
                setSettings(result.data);
                        
                // Parse gate schedule if it exists and restrictions are enabled
                if (result.data.SettingGateOpeningRestrictions && result.data.SettingGateOpeningSchedule) {
                    try {
                        const schedule = JSON.parse(result.data.SettingGateOpeningSchedule);
                        setGateSchedule(schedule);
                    } catch (e) {
                        console.error('Error parsing gate schedule:', e);
                        setGateSchedule(DEFAULT_GATE_SCHEDULE);
                    }
                } else {
                    setGateSchedule(DEFAULT_GATE_SCHEDULE);
                }

                // Check sharing of plates, pins, QR codes, and face profiles
                fetchSharingStatus();
            })
            .catch(error => {
                setLoading(false);
                console.error('Error fetching data:', error);
            });

        // Populate kiosks dropdown
        axios.get('/api/kiosks')
            .then(response => {
                if (response.data.length === 0) {
                    throw new Error('No kiosks available');
                }
                response.data = response.data.filter(kiosk => kiosk.id !== globalTenantId);
                response.data.unshift({id: 'none', OrganisationName: 'None'});
                setKiosks(response.data);
            })
            .catch(error => {
                console.error('Error fetching kiosks:', error);
            });

        // Populate kiosk types dropdown
        axios.get('/api/kiosk-types')
            .then(response => {
                if (response.data.length === 0) {
                    throw new Error('No kiosk types available');
                }
                setKioskTypes(response.data);
            })
            .catch(error => {
                console.error('Error fetching kiosk types:', error);
            });

        // Populate kiosk themes dropdown
        axios.get('/api/kiosk-themes')
            .then(response => {
                if (response.data.length === 0) {
                    throw new Error('No kiosk themes available');
                }
                setKioskThemes(response.data);
            })
            .catch(error => {
                console.error('Error fetching kiosk themes:', error);
            });

        // Fetch server kiosk UI
        setServerKioskUIFromAPI();

        return () => {
            console.log('removing handleunload');
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, [globalTenantId]);

    const fetchSharingStatus = () => {
        axios.get(`/api/number-plates/is-shared?tenantId=${globalTenantId}`)
            .then(result => {
                if (result.data.SharePlates && result.data.SharingPlatesMasterTenantID != globalTenantId) {
                    setDisableNumberPlateAliases(true);
                    setMasterTenantNameForNumberPlates(result.data.masterTenantName);
                    setMasterPlateAliases(result.data.masterPlateAliases);
                } else {
                    setMasterPlateAliases(null);
                    setDisableNumberPlateAliases(false);
                }
            }).catch(error => {
                console.error('Error fetching sharing of plates:', error);
            });

        axios.get(`/api/pins/is-shared?tenantId=${globalTenantId}`)
            .then(result => {
                if (result.data.SharePINs && result.data.SharingPINsMasterTenantID != globalTenantId) {
                    setDisablePINAliases(true);
                    setMasterTenantNameForPINs(result.data.masterTenantName);
                    setMasterPINAliases(result.data.masterPINAliases);
                } else {
                    setMasterPINAliases(null);
                    setDisablePINAliases(false);
                }
            }).catch(error => {
                console.error('Error fetching sharing of PINs:', error);
            });

        axios.get(`/api/qr-code-keys/is-shared?tenantId=${globalTenantId}`)
            .then(result => {
                if (result.data.ShareQrCodeKeys && result.data.SharingQrCodeKeysMasterTenantID != globalTenantId) {
                    setDisableQrCodeKeysAliases(true);
                    setMasterTenantNameForQrCodeKeys(result.data.masterTenantName);
                    setMasterQrCodeKeyAliases(result.data.masterQrCodeKeyAliases);
                } else {
                    setMasterQrCodeKeyAliases(null);
                    setDisableQrCodeKeysAliases(false);
                }
            }).catch(error => {
                console.error('Error fetching sharing of QR codes:', error);
            });

        axios.get(`/api/face-profiles/is-shared?tenantId=${globalTenantId}`)
            .then(result => {
                if (result.data.ShareFaceProfiles && result.data.SharingFaceProfilesMasterTenantID != globalTenantId) {
                    setDisableFaceProfileAliases(true);
                    setMasterTenantNameForFaceProfiles(result.data.masterTenantName);
                    setMasterFaceProfileAliases(result.data.masterFaceProfileAliases);
                } else {
                    setMasterFaceProfileAliases(null);
                    setDisableFaceProfileAliases(false);
                }
            }).catch(error => {
                console.error('Error fetching sharing of face profiles:', error);
            });
    };

    const handleNavigationAttempt = (checkOnly = false) => {
        if (!isModified) return true;
        
        if (checkOnly) {
            return false;
        }
        
        return window.confirm('You have unsaved changes. Do you want to leave this page? Your changes will be lost.');
    };

    const handleTabChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        let newValue;

        if (type === 'checkbox') {
            newValue = checked;
            if (name === 'SettingGateOpeningRestrictions' && !checked) {
                setGateSchedule(DEFAULT_GATE_SCHEDULE);
            }
        } else if (name === 'securityLevel') {
            newValue = value === '' ? null : value;
        } else {
            newValue = value;
        }

        setSettings(prevSettings => ({
            ...prevSettings,
            [name]: newValue
        }));
        setIsModified(true);
    };

    const handleScheduleChange = (day, field, value) => {
        setIsModified(true);
        const newSchedule = {
            ...gateSchedule,
            [day]: {
                ...gateSchedule[day],
                [field]: value
            }
        };
        setGateSchedule(newSchedule);
        setSettings(prev => ({
            ...prev,
            SettingGateOpeningSchedule: JSON.stringify(newSchedule)
        }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        
        if (!settings.SettingAllowBluetoothRelay && settings.BluetoothPasscode && 
            !window.confirm("You are unsetting the Bluetooth relay option, but have a Bluetooth passcode set. " + 
            `If you continue, you will not be able to reuse the relay without the current passcode, which is ${settings.BluetoothPasscode}. You must take ` + 
            `note of this passcode in case you want to reset the relay in the future. Are you sure you want to continue?`)) {
            return;
        }

        setLoading(true);

        const { BluetoothPasscode, ResetBluetoothCustomPasscode, ResetBluetooth, KioskUniqueLoginToken, Reboot, ResetDevicePIN, Unlock, ...settingsToSend } = settings;
        
        if (!settings.SettingAllowBluetoothRelay) {
            settings.BluetoothPasscode = null;
            settingsToSend.BluetoothPasscode = null;
            settingsToSend.ResetBluetoothCustomPasscode = null;
        }

        axios.put(`/api/tenants/${settings.id}`, settingsToSend)
            .then(() => {
                axios.post(`/api/refreshKioskUI`, {tenantId: globalTenantId, room: 'Room'})
                    .then(result => {
                        console.log('Kiosk UI refresh request sent');
                    })
                    .catch(error => { 
                        console.error('Error sending Kiosk UI refresh request:', error);
                    });
                setLoading(false);
                setIsModified(false);
            })
            .catch(error => {
                setLoading(false);
                if (error.response && error.response.data && error.response.data.message) {
                    alert(error.response.data.message);
                } else {
                    console.error('Error saving data:', error);
                    alert('An unexpected error occurred.');
                }
            });
    };

    const handleCustomKioskUIChange = (e) => {
        const checked = e.target.checked;
        if (checked) {
            setOpenWarningDialog(true);
        } else {
            handleInputChange(e);
        }
    };

    const handleRevertToOriginal = () => {
        setOpenRevertWarningDialog(true);
    };

    const confirmRevertToOriginal = async () => {
        console.log('reverting...');
        let serverData = await setServerKioskUIFromAPI();
        setSettings(prevSettings => ({
            ...prevSettings,
            SettingKioskUI: serverData
        }));
        setIsModified(true);
        setOpenRevertWarningDialog(false);
    };

    const updateKioskUI = async (e) => {
        console.log('updating kiosk UI');
        let serverData = await setServerKioskUIFromAPI(e.target.value);
        console.log('reverting...');
        setSettings(prevSettings => ({
            ...prevSettings,
            SettingKioskUI: serverData
        }));
        setIsModified(true);
        setOpenRevertWarningDialog(false);
    };

    const handleOpenGallery = () => {
        setIsGalleryOpen(true);
    };

    const handleCloseGallery = () => {
        setIsGalleryOpen(false);
    };

    const handleImageSelect = (url) => {
        setIsModified(true);
        setSettings(prevSettings => ({
            ...prevSettings,
            SettingCustomKioskThemeBackgroundImageURL: url
        }));
        handleCloseGallery();
    };

    const handleLandscapeImageSelect = (url) => {
        setIsModified(true);
        setSettings(prevSettings => ({
            ...prevSettings,
            SettingCustomKioskThemeLandscapeBackgroundImageURL: url
        }));
        handleCloseGallery();
    };

    const handleOpenKioskUIGallery = () => {
        setIsKioskUIGalleryOpen(true);
    };

    const handleCloseKioskUIGallery = () => {
        setIsKioskUIGalleryOpen(false);
    };

    const handleKioskUIImageSelect = (url) => {
        // Only allow insertion if we have a valid cursor position
        if (kioskUICursorPosition === null) {
            handleCloseKioskUIGallery();
            return;
        }
        
        setIsModified(true);
        
        // Create an img tag with the selected URL
        const imgTag = `<img src="${url}" alt="" />`;
        
        const currentValue = settings.SettingKioskUI || '';
        const { start, end } = kioskUICursorPosition;
        
        // Insert at the stored cursor position
        const newValue = currentValue.substring(0, start) + imgTag + currentValue.substring(end);
        
        setSettings(prevSettings => ({
            ...prevSettings,
            SettingKioskUI: newValue
        }));
        
        // Restore focus and cursor position after update
        const textarea = kioskUITextareaRef;
        setTimeout(() => {
            if (textarea) {
                textarea.focus();
                const newCursorPos = start + imgTag.length;
                textarea.setSelectionRange(newCursorPos, newCursorPos);
                // Update stored cursor position
                setKioskUICursorPosition({ start: newCursorPos, end: newCursorPos });
            }
        }, 0);
        
        handleCloseKioskUIGallery();
    };

    const handleAccordionToggle = () => {
        setIsAccordionExpanded(!isAccordionExpanded);
    };

    const handleCustomTheme = () => {
        setShowThemeDialog(false);

        axios.get(`/api/kiosk-themes?id=${kioskThemes.find(theme => theme.id === customKioskThemeID).id}`)
            .then(response => {
                if (response.data.length === 0) {
                    throw new Error('No kiosk themes available');
                }
                console.log(response.data);
                setSettings(prevSettings => ({
                    ...prevSettings,
                    SettingCustomKioskTheme: response.data.css,
                    SettingCustomKioskThemeBackgroundImageURL: response.data.BackgroundImageURL,
                    SettingCustomKioskThemeLandscapeBackgroundImageURL: response.data.LandscapeBackgroundImageURL,
                    SettingCustomKioskThemeColour1: response.data.Colour1,
                    SettingCustomKioskThemeColour2: response.data.Colour2,
                    SettingCustomKioskThemeColour3: response.data.Colour3,
                    SettingCustomKioskThemeColour4: response.data.Colour4,
                    SettingCustomKioskThemeColour5: response.data.Colour5,
                    SettingCustomKioskThemeColour6: response.data.Colour6,
                }));
                setIsModified(true);
            })
            .catch(error => {
                console.error('Error fetching kiosk themes:', error);
            });
    };

    const handleCustomThemeCancel = () => {
        setShowThemeDialog(false);
        if (!settings.SettingCustomKioskTheme) {
            console.log('setting back to ' + previousCustomKioskThemeID);
            setSettings(prevSettings => ({
                ...prevSettings,
                KioskThemeID: previousCustomKioskThemeID
            }));
        }
    };

    const resetCustomTheme = () => {
        if (!window.confirm('Are you sure you want to reset the custom theme? You will lose any customisations made to the theme.')) return;
        setShowThemeDialog(true);
    };

    const showBlueToothDialog = () => {
        axios.get(`/api/tenants-bluetoothpasscode?tenantId=${globalTenantId}`)
            .then(response => {
                if (response.data.length === 0) {
                    throw new Error('Could not get passcode');
                }
                setSettings(prevSettings => ({
                    ...prevSettings,
                    ResetBluetooth: response.data.ResetBluetooth,
                    BluetoothPasscode: response.data.passcode
                }));
                setBluetoothResetOption(response.data.passcode ? 'basic' : 'reset');
                setShowResetBluetoothDialog(true);
            })
            .catch(error => {
                console.error('Error fetching kiosk themes:', error);
            });
    };

    const confirmResetBluetooth = () => {
        if (bluetoothResetOption !== 'cancel' && !window.confirm('Are you sure you want to reset the bluetooth device? You will need to forcibly reset and reload the app on the Kiosk to enter the Bluetooth setup screen.')) return;

        axios.post(`/api/tenants-resetbluetooth`, {tenantId: globalTenantId, resetType: bluetoothResetOption, passcode: bluetoothCustomPasscode})
            .then(result => {
                setShowResetBluetoothDialog(false);
                console.log('Bluetooth reset request sent');
            })
            .catch(error => { 
                console.error('Error sending Bluetooth reset request:', error);
            });
    };

    // Find current theme
    const foundTheme = kioskThemes.find(theme => theme.id === settings?.KioskThemeID);

    return {
        // State
        settings,
        setSettings,
        loading,
        isModified,
        setIsModified,
        serverKioskUI,
        globalTenantId,
        user,
        value,
        kiosks,
        kioskTypes,
        kioskThemes,
        foundTheme,
        
        // Dialog states
        openWarningDialog,
        setOpenWarningDialog,
        openRevertWarningDialog,
        setOpenRevertWarningDialog,
        showThemeDialog,
        setShowThemeDialog,
        showResetBluetoothDialog,
        setShowResetBluetoothDialog,
        showDiffDialog,
        setShowDiffDialog,
        showLockdownAppSettingsDialog,
        setShowLockdownAppSettingsDialog,
        showMovementZonesDialog,
        setShowMovementZonesDialog,
        showANPRMovementZonesDialog,
        setShowANPRMovementZonesDialog,
        showDebugLogDialog,
        setShowDebugLogDialog,
        
        // Bluetooth states
        bluetoothResetOption,
        setBluetoothResetOption,
        bluetoothCustomPasscode,
        setBluetoothCustomPasscode,
        
        // Alias states
        disableNumberPlateAliases,
        disablePINAliases,
        disableQrCodeKeysAliases,
        disableFaceProfileAliases,
        masterPlateAliases,
        masterPINAliases,
        masterQrCodeKeyAliases,
        masterFaceProfileAliases,
        masterTenantNameForNumberPlates,
        masterTenantNameForPINs,
        masterTenantNameForQrCodeKeys,
        masterTenantNameForFaceProfiles,
        
        // Gallery and accordion states
        isGalleryOpen,
        isKioskUIGalleryOpen,
        setKioskUITextareaRef,
        kioskUICursorPosition,
        setKioskUICursorPosition,
        isAccordionExpanded,
        
        // Theme states
        customKioskThemeID,
        setCustomKioskThemeID,
        previousCustomKioskThemeID,
        setPreviousCustomKioskThemeID,
        
        // Gate schedule states
        gateSchedule,
        gateTimesDialogOpen,
        setGateTimesDialogOpen,
        
        // Debug log states
        debugLog,
        setDebugLog,
        loadingDebugLog,
        setLoadingDebugLog,
        
        // Handlers
        handleNavigationAttempt,
        handleTabChange,
        handleInputChange,
        handleScheduleChange,
        handleSave,
        handleCustomKioskUIChange,
        handleRevertToOriginal,
        confirmRevertToOriginal,
        updateKioskUI,
        handleOpenGallery,
        handleCloseGallery,
        handleImageSelect,
        handleLandscapeImageSelect,
        handleOpenKioskUIGallery,
        handleCloseKioskUIGallery,
        handleKioskUIImageSelect,
        handleAccordionToggle,
        handleCustomTheme,
        handleCustomThemeCancel,
        resetCustomTheme,
        showBlueToothDialog,
        confirmResetBluetooth,
        
        // Utilities
        axios,
    };
};

export default useSettings;

